import type { AxiosError, AxiosResponse } from "axios"
import type { AddEmailsPayload, Customer, CustomerId, DeleteEmailPayload } from "types/customer"
import axios from "axios"
import config from "config"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import useAccessToken from "./useAccessToken"

const sherlockPath = config.sherlockApiBasePath

export const sherlockQueryKey = (customerId: CustomerId) => ["sherlock", customerId]

type Props = {
  customerId: CustomerId
  enabled?: boolean
}

export function useSherlock({ customerId, enabled = true }: Props) {
  const getCustomerDataUrl = `${sherlockPath}/${customerId}`
  const addFeatureUrl = `${sherlockPath}/${customerId}/add-feature`
  const removeFeatureUrl = `${sherlockPath}/${customerId}/remove-feature`
  const addEmailsUrl = `${sherlockPath}/${customerId}/add-emails`
  const deleteEmailUrl = `${sherlockPath}/${customerId}/delete-email`

  const queryClient = useQueryClient()
  const token = useAccessToken()

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } }
  // Keyed by customer only: the config does not depend on the token, and a
  // token-keyed entry turned every token renewal into a cold load — which
  // AppWrapper shows as the full-screen spinner, unmounting the whole app.
  const sherlockQueryId = sherlockQueryKey(customerId)

  const sherlockQuery = useQuery({
    queryKey: sherlockQueryId,
    queryFn: async () => axios.get<Customer>(getCustomerDataUrl, axiosConfig).then((res) => res.data),
    enabled: enabled && !!customerId && !!token,
    staleTime: Infinity,
    refetchOnWindowFocus: false
  })

  // TODO: better typing for these mutations
  const setSherlockQueryData = ({ data }: { data: any }) => {
    queryClient.setQueryData(sherlockQueryId, data)
  }

  // TODO: better optimistic renders here using onMutate instead of onSuccess
  const addFeatureMutation = useMutation({
    mutationFn: async (feature: string) => axios.post(addFeatureUrl, { feature }, axiosConfig),
    onSuccess: setSherlockQueryData
  })

  const deleteFeatureMutation = useMutation({
    mutationFn: async (feature: string) => axios.post(removeFeatureUrl, { feature }, axiosConfig),
    onSuccess: setSherlockQueryData
  })

  const addEmailsMutation = useMutation({
    mutationFn: async (payload: AddEmailsPayload) => axios.post(addEmailsUrl, payload, axiosConfig),
    onSuccess: setSherlockQueryData
  })

  const deleteEmailMutation = useMutation({
    mutationFn: async (payload: DeleteEmailPayload) => axios.post(deleteEmailUrl, payload, axiosConfig),
    onSuccess: setSherlockQueryData
  })

  return {
    ...sherlockQuery,
    addFeatureMutation,
    deleteFeatureMutation,
    addEmailsMutation,
    deleteEmailMutation,
  }
}
